/**
 * Phase 66 Plan 02 — Centralized TanStack Query key factory.
 *
 * THIS IS THE MULTI-TENANT CACHE-KEY CONTRACT. Every query hook in
 * Plans 03 / 04 / 05a / 05b / 06 builds keys via the `qk.*` factories
 * defined here — hand-rolled `queryKey: [...]` literals are FORBIDDEN.
 *
 * Why: RESEARCH §Pitfall 8 + threat T-66-04-07 — circuit_id MUST be
 * baked into every cached entry's key. Otherwise switching tenants
 * (Plan 06 TenantSwitcher) leaks records from the previous tenant's
 * scope until the next refetch. Plan 06 swaps `useCircuitId()` from a
 * stub (returning null) to a real read of `?circuit_id=` URL param +
 * localStorage; with this factory in place, every existing cache entry
 * is automatically invalidated by the changed key.
 *
 * Convention:
 *   qk.X(circuitId, ...rest) — circuitId is ALWAYS the first non-namespace
 *                              element. Use `"all"` for super_admin
 *                              viewing the global scope (no filter).
 *
 *   qk.circuits()            — the only entity NOT scoped by circuitId
 *                              (it IS the list of circuits the user
 *                              can switch between).
 *
 * Adding a new entity? Add a factory here. Do NOT hand-roll the key
 * inline. The Vitest test in `__tests__/queryKeys.test.ts` enforces the
 * tenant-isolation invariant.
 *
 * @see RESEARCH.md §Pitfall 8 — Multi-tenant cache-key isolation
 * @see threat register T-66-04-07
 */

export const qk = {
  machines: (circuitId: string | "all") => ["machines", circuitId] as const,
  users: (circuitId: string | "all") => ["users", circuitId] as const,
  vpn: (circuitId: string | "all") => ["vpn", circuitId] as const,
  provisioning: (circuitId: string | "all") => ["provisioning", circuitId] as const,
  audit: (circuitId: string | "all", filters: object) =>
    ["audit", circuitId, filters] as const,
  /** The documented exception: circuits is the list of tenants itself. */
  circuits: () => ["circuits"] as const,
  minioBuckets: (circuitId: string | "all") => ["minio", "buckets", circuitId] as const,
  minioObjects: (circuitId: string | "all", bucket: string, prefix: string) =>
    ["minio", "objects", circuitId, bucket, prefix] as const,
  srvfs: (circuitId: string | "all", root: string, subpath: string) =>
    ["srvfs", circuitId, root, subpath] as const,
  kioskListDir: (circuitId: string | "all", cpuId: string, path: string) =>
    ["kiosk", "listDir", circuitId, cpuId, path] as const,
  pgExportTables: (circuitId: string | "all") =>
    ["pgexport", "tables", circuitId] as const,
  /**
   * Phase 68 power-health namespace.
   *
   * Pitfall 8 multi-tenant isolation: every key bakes `circuitId` as the
   * first non-namespace element (mirrors the qk.machines / qk.audit pattern).
   * The lone exception is `powerHealthSunTimes` — sunrise/sunset is a pure
   * machine-level computation (lat/lon → astronomical fact) and does NOT
   * leak operational data across tenants, so the key is `[ns, action,
   * cpuId, date]` without circuitId.
   *
   * `filters` is the FilterBar form snapshot (rangeDays + severityMin +
   * flaggedOnly + kioskIds) for heatmap. `events` filters include type +
   * severity. The `keepPreviousData` placeholder in usePowerHealth keeps
   * the heatmap painted while a new key resolves (UI-SPEC §6.1).
   */
  powerHealthSnapshots: (
    circuitId: string | "all",
    cpuId: string,
    date: string,
  ) => ["powerHealth", "snapshots", circuitId, cpuId, date] as const,
  powerHealthEvents: (
    circuitId: string | "all",
    cpuId: string,
    filters: object,
  ) => ["powerHealth", "events", circuitId, cpuId, filters] as const,
  powerHealthHeatmap: (
    circuitId: string | "all",
    days: number,
    filters: object,
  ) => ["powerHealth", "heatmap", circuitId, days, filters] as const,
  powerHealthTimeline: (
    circuitId: string | "all",
    cpuId: string,
    hours: number,
  ) => ["powerHealth", "timeline", circuitId, cpuId, hours] as const,
  powerHealthSunTimes: (cpuId: string, date: string) =>
    ["powerHealth", "sunTimes", cpuId, date] as const,
  powerHealthSummary: (circuitId: string | "all") =>
    ["powerHealth", "summary", circuitId] as const,
  powerInstabilityFlagged: (circuitId: string | "all") =>
    ["powerHealth", "flaggedCount", circuitId] as const,
  /**
   * Phase 69 admin-users namespace.
   *
   * Pitfall 8 multi-tenant isolation: `adminUsers` + `adminUser` bake
   * `circuitId` as the 2nd element (after the namespace label). Super
   * admin uses `"all"`; circuit_admin uses its own circuit_id. Tenant
   * leak prevention is enforced by the vitest test in
   * `__tests__/queryKeys.test.ts` (mirrors the audit/machines invariants).
   *
   * The lone exception is `adminUserMe2FA` — it is a per-user resource
   * (the current authenticated user's 2FA state) and never tenant-scoped.
   * Mirrors the `powerHealthSunTimes` precedent (machine-level fact).
   */
  adminUsers: (circuitId: string | "all", filters: object) =>
    ["adminUsers", circuitId, filters] as const,
  adminUser: (circuitId: string | "all", id: string) =>
    ["adminUsers", "detail", circuitId, id] as const,
  adminUserMe2FA: () =>
    ["adminUsers", "me", "2fa"] as const,
  /**
   * Phase 70 machine clone templates namespace.
   *
   * Pitfall 8 multi-tenant isolation: `machineCloneTemplates` +
   * `machineCloneTemplate` + `machineClusterLabels` bake `circuitId` as the
   * 2nd element. Super_admin uses `"all"`; circuit_admin uses its own
   * circuit_id. Backend re-validates tenant scope on every endpoint.
   *
   * `filters` captures the cluster_label / search snapshot so different
   * filter views cache separately.
   */
  machineCloneTemplates: (circuitId: string | "all", filters: object = {}) =>
    ["machineCloneTemplates", circuitId, filters] as const,
  machineCloneTemplate: (circuitId: string | "all", id: string) =>
    ["machineCloneTemplates", "detail", circuitId, id] as const,
  machineClusterLabels: (circuitId: string | "all") =>
    ["machineClusterLabels", circuitId] as const,
  /**
   * Phase 72 digital twin namespace.
   *
   * Pitfall 8 multi-tenant isolation: `digitalTwin` + `digitalTwinVersions`
   * bake `circuitId` as the 2nd element. Super_admin uses `"all"`;
   * circuit_admin uses its own circuit_id. Backend re-validates tenant
   * scope on every endpoint of /admin/machines/{cpu_id}/digital-twin/*.
   *
   * Prefix-match invalidation works because both keys share the
   * `["digitalTwin", ...]` prefix; invalidating `["digitalTwin"]` evicts
   * both the summary AND the versions list in one call.
   */
  digitalTwin: (circuitId: string | "all", cpuId: string) =>
    ["digitalTwin", circuitId, cpuId] as const,
  digitalTwinVersions: (circuitId: string | "all", cpuId: string) =>
    ["digitalTwin", "versions", circuitId, cpuId] as const,
  /**
   * Phase 80-04a circuit API-keys namespace.
   *
   * Pitfall 8 multi-tenant isolation: `circuitApiKeys` bakes the target
   * circuit_id as the 2nd element. The keys panel is super_admin-only, but
   * the key list is still strictly scoped to ONE circuit, so the cache entry
   * must never be shared across circuits. NOTE: the cached value is the
   * NON-SECRET metadata list only — the show-once plaintext token is held in
   * component state and is NEVER written to the query cache.
   */
  circuitApiKeys: (circuitId: string) =>
    ["circuitApiKeys", circuitId] as const,
} as const;

export type QueryKey = ReturnType<(typeof qk)[keyof typeof qk]>;
